import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Search, Wrench, Eye, X, Plus, Minus } from 'lucide-react';
import api from '../../services/api';
import { PageHeader, TableSkeleton, EmptyState, Pagination } from '../../components/PageParts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/format';

const STATUSES = ['Aberta', 'Aguardando Aprovação', 'Em Andamento', 'Pausada', 'Aguardando Peças', 'Finalizada', 'Cancelada'];
const PRIORITIES = [{ v: 'baixa', l: 'Baixa' }, { v: 'normal', l: 'Normal' }, { v: 'alta', l: 'Alta' }, { v: 'urgente', l: 'Urgente' }];

const statusTone = (s) => ({
  'Aberta': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'Aguardando Aprovação': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  'Em Andamento': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  'Pausada': 'bg-neutral-200 text-neutral-700',
  'Aguardando Peças': 'bg-amber-100 text-amber-800',
  'Finalizada': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'Cancelada': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
}[s] || 'bg-neutral-200 text-neutral-700');

const priorityTone = (p) => ({
  baixa: 'text-neutral-500', normal: 'text-blue-600', alta: 'text-amber-600', urgente: 'text-red-600',
}[p] || 'text-neutral-500');

const emptyItem = { kind: 'part', description: '', quantity: 1, unit_value: 0, total: 0 };
const emptyForm = { vehicle_id: '', priority: 'normal', maintenance_type_name: '', workshop: '', responsible_name: '', reported_defect: '', diagnosis: '', notes: '', items: [] };

export default function MaintenanceOrders() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('all');
  const [priorityF, setPriorityF] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [vehicles, setVehicles] = useState([]);
  const [types, setTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (statusF !== 'all') params.status = statusF;
      if (priorityF !== 'all') params.priority = priorityF;
      const { data } = await api.get('/maintenance-orders', { params });
      setItems(data.items); setTotal(data.total);
    } catch { toast.error('Erro'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusF, priorityF]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  useEffect(() => {
    (async () => {
      const [v, mt, sup] = await Promise.all([
        api.get('/vehicles', { params: { limit: 200 } }),
        api.get('/masters/maintenance-types', { params: { limit: 100 } }).catch(() => ({ data: { items: [] } })),
        api.get('/suppliers', { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
      ]);
      setVehicles(v.data.items || []); setTypes(mt.data.items || []); setSuppliers(sup.data.items || []);
    })();
  }, []);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, k, v) => setForm(f => {
    const items = [...f.items];
    items[i] = { ...items[i], [k]: v };
    if (k === 'quantity' || k === 'unit_value') items[i].total = (Number(items[i].quantity) || 0) * (Number(items[i].unit_value) || 0);
    return { ...f, items };
  });

  const totalCost = form.items.reduce((s, it) => s + (Number(it.total) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error('Selecione um veículo'); return; }
    setSaving(true);
    try {
      await api.post('/maintenance-orders', form);
      toast.success('OS criada');
      setModalOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/maintenance-orders/${deleteId}`); toast.success('OS excluída'); setDeleteId(null); load(); }
    catch { toast.error('Erro'); }
  };

  return (
    <div>
      <PageHeader title="Ordens de Serviço" subtitle="Gestão completa das OS" onAction={() => { setForm(emptyForm); setModalOpen(true); }} actionLabel="Nova OS" />

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input data-testid="os-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por número, veículo, defeito..." className="pl-9" />
          </div>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger data-testid="os-status-filter" className="w-full md:w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priorityF} onValueChange={setPriorityF}>
            <SelectTrigger className="w-full md:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Prioridade</SelectItem>
              {PRIORITIES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
          {(search || statusF !== 'all' || priorityF !== 'all') && <Button variant="outline" onClick={() => { setSearch(''); setStatusF('all'); setPriorityF('all'); }}><X className="h-4 w-4 mr-1" />Limpar</Button>}
        </div>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Nº OS</th>
                <th className="text-left px-4 py-3">Veículo</th>
                <th className="text-left px-4 py-3">Defeito</th>
                <th className="text-left px-4 py-3">Prioridade</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Custo total</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="os-table-body" className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? <tr><td colSpan={7} className="p-4"><TableSkeleton rows={5} cols={7} /></td></tr> :
                items.length === 0 ? <tr><td colSpan={7}><EmptyState icon={Wrench} title="Nenhuma OS" description="Crie a primeira ordem de serviço." /></td></tr> :
                items.map(os => (
                  <tr key={os.id} className="tr-hover">
                    <td className="px-4 py-3 font-mono text-xs font-semibold">{os.number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium font-mono text-xs">{os.vehicle_plate}</div>
                      <div className="text-[10px] text-neutral-500">{os.maintenance_type_name || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs max-w-xs truncate">{os.reported_defect || '-'}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-medium ${priorityTone(os.priority)}`}>{os.priority}</span></td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone(os.os_status)}`}>{os.os_status}</span></td>
                    <td className="px-4 py-3 text-right font-medium text-xs">{formatCurrency((os.costs?.total) || 0)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button data-testid={`view-os-${os.number}`} size="icon" variant="ghost" onClick={() => navigate(`/manutencao/os/${os.id}`)}><Eye className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setDeleteId(os.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} limit={10} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Ordem de Serviço</DialogTitle>
            <DialogDescription>Preencha os dados iniciais. Você pode editar detalhes e mudar status na página da OS.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5 md:col-span-2">
                <Label>Veículo *</Label>
                <Select value={form.vehicle_id} onValueChange={v => setForm(f => ({ ...f, vehicle_id: v }))}>
                  <SelectTrigger data-testid="os-vehicle-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate} - {v.brand} {v.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Prioridade *</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo de Manutenção</Label>
                <Select value={form.maintenance_type_id || 'none'} onValueChange={v => {
                  if (v === 'none') { setForm(f => ({ ...f, maintenance_type_id: '', maintenance_type_name: '' })); return; }
                  const t = types.find(x => x.id === v);
                  setForm(f => ({ ...f, maintenance_type_id: v, maintenance_type_name: t?.name || '' }));
                }}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Oficina/Fornecedor</Label>
                <Select value={form.supplier_id || 'none'} onValueChange={v => {
                  if (v === 'none') { setForm(f => ({ ...f, supplier_id: '', supplier_name: '', workshop: '' })); return; }
                  const s = suppliers.find(x => x.id === v);
                  setForm(f => ({ ...f, supplier_id: v, supplier_name: s?.name || '', workshop: s?.name || '' }));
                }}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Interna —</SelectItem>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Responsável</Label>
                <Input value={form.responsible_name} onChange={e => setForm(f => ({ ...f, responsible_name: e.target.value }))} />
              </div>
              <div className="space-y-1.5 md:col-span-3">
                <Label>Defeito informado *</Label>
                <Textarea data-testid="os-defect" required rows={2} value={form.reported_defect} onChange={e => setForm(f => ({ ...f, reported_defect: e.target.value }))} />
              </div>
              <div className="space-y-1.5 md:col-span-3">
                <Label>Diagnóstico inicial</Label>
                <Textarea rows={2} value={form.diagnosis} onChange={e => setForm(f => ({ ...f, diagnosis: e.target.value }))} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Custos discriminados</Label>
                <Button data-testid="add-os-item" type="button" size="sm" variant="outline" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" />Adicionar</Button>
              </div>
              <div className="space-y-2">
                {form.items.length === 0 && <p className="text-xs text-neutral-500">Nenhum item lançado ainda.</p>}
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      <Select value={it.kind} onValueChange={v => updateItem(i, 'kind', v)}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="labor">Mão de obra</SelectItem>
                          <SelectItem value="part">Peça</SelectItem>
                          <SelectItem value="service">Serviço</SelectItem>
                          <SelectItem value="other">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-4"><Input placeholder="Descrição" value={it.description} onChange={e => updateItem(i, 'description', e.target.value)} className="h-9" /></div>
                    <div className="col-span-1"><Input type="number" step="0.01" value={it.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="h-9" /></div>
                    <div className="col-span-2"><Input type="number" step="0.01" placeholder="Valor" value={it.unit_value} onChange={e => updateItem(i, 'unit_value', e.target.value)} className="h-9" /></div>
                    <div className="col-span-1 text-xs text-right font-medium py-2">{formatCurrency(it.total || 0)}</div>
                    <div className="col-span-1"><Button type="button" size="icon" variant="ghost" className="text-red-600 h-9 w-9" onClick={() => removeItem(i)}><Minus className="h-4 w-4" /></Button></div>
                  </div>
                ))}
              </div>
              {form.items.length > 0 && <div className="mt-2 text-sm font-semibold text-right">Total: {formatCurrency(totalCost)}</div>}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button data-testid="save-os" type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Criar OS'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir OS?</AlertDialogTitle><AlertDialogDescription>Soft delete — pode ser restaurado.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
